import React, { useEffect, useRef, useState } from "react";

import { requestJson } from "./storiesHelpers";
import useAnchoredPopover from "./useAnchoredPopover";

const decodeHtmlEntities = (text) => new DOMParser().parseFromString(text, "text/html").documentElement.textContent;

const fetchTranslation = async ({ baseLanguageIso, targetLanguageIso, translationUrl, word }) => {
  const response = await requestJson({ data: { from: targetLanguageIso, to: baseLanguageIso, translate: word }, method: "post", url: translationUrl });
  return (response?.translations || []).map((translation) => decodeHtmlEntities(translation.translatedText)).join("; ");
};

export default function StoryWordPanel({ anchor, baseLanguageIso, dictionaryLinks, isSaved, isSaving, targetLanguageIso, translationUrl, word, onClose, onSave, onSentenceSearch }) {
  const [translation, setTranslation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const panelRef = useRef(null);
  const style = useAnchoredPopover({ anchor, panelRef, onClose });

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    const loadTranslation = async () => {
      try {
        const translated = await fetchTranslation({ baseLanguageIso, targetLanguageIso, translationUrl, word });
        if (isCurrent) setTranslation(translated);
      } catch {
        if (isCurrent) setTranslation(null);
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };
    loadTranslation();
    return () => {
      isCurrent = false;
    };
  }, [word]);

  return (
    <div aria-label="Word" className="story-sentence-popover story-word-popover" data-testid="story-word-panel" ref={panelRef} role="dialog" style={style}>
      {isLoading ? (
        <div className="story-word-panel-loading" data-testid="story-word-panel-loading">
          <span className="fa fa-circle-o-notch fa-spin" />
        </div>
      ) : (
        <>
          <div>
            <p className="story-word-panel-word" data-testid="story-word-panel-word">{word}</p>
            <p className="story-sentence-popover-translation" data-testid="story-word-panel-translation">{translation || "No translation found"}</p>
          </div>
          <div className="story-word-panel-links" data-testid="story-word-panel-links">
            {dictionaryLinks.map((link) => (
              <a href={link.url} key={link.name} rel="noopener noreferrer" target="_blank">
                {link.name} <span className="fa fa-external-link" />
              </a>
            ))}
          </div>
          <div className="story-sentence-popover-actions">
            <button className="story-sentence-tile" data-testid="story-word-save" disabled={isSaved || isSaving} type="button" onClick={onSave}>
              <span className={`fa fa-bookmark${isSaved ? "" : "-o"}`} />
              {isSaved ? "Saved" : "Save to learn"}
            </button>
            <button className="story-sentence-tile" data-testid="story-word-sentence-search" type="button" onClick={onSentenceSearch}>
              <span className="fa fa-search" />
              Sentence Search
            </button>
          </div>
        </>
      )}
    </div>
  );
}
